import { useRef, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Play, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Check = { name: string; ok: boolean; detail?: string; critical?: boolean };
type Report = { ok: boolean; mode: string; total: number; passed: number; failed: number; booking_id?: string; checks: Check[] };

export default function AdminProdSmokeTest() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [finishedAt, setFinishedAt] = useState<string | null>(null);
  const [durationMs, setDurationMs] = useState<number | null>(null);
  const [log, setLog] = useState<{ ts: string; msg: string }[]>([]);
  const logRef = useRef<{ ts: string; msg: string }[]>([]);

  const append = (msg: string) => {
    const e = { ts: new Date().toISOString(), msg };
    logRef.current = [...logRef.current, e];
    setLog(logRef.current);
  };

  const run = async () => {
    setRunning(true); setReport(null);
    logRef.current = []; setLog([]);
    const t0 = Date.now();
    const startIso = new Date(t0).toISOString();
    setStartedAt(startIso); setFinishedAt(null); setDurationMs(null);
    append("Invoking prod-smoke-test edge function (LIVE mode)…");
    try {
      const { data, error } = await supabase.functions.invoke("prod-smoke-test");
      if (error || (data as any)?.error) throw new Error(error?.message || (data as any)?.error);
      const r = data as Report;
      setReport(r);
      append(`Mode: ${r.mode.toUpperCase()} • booking_id: ${r.booking_id ?? "n/a"}`);
      for (const c of r.checks) append(`${c.ok ? "PASS" : "FAIL"} — ${c.name}${c.detail ? `  [${c.detail}]` : ""}`);
      append(`Summary: ${r.passed}/${r.total} passed, ${r.failed} failed`);
      r.ok ? toast.success(`All ${r.total} checks passed`) :
        toast.error(`${r.failed} of ${r.total} failed`);
    } catch (e) {
      append(`ERROR: ${(e as Error).message}`);
      toast.error((e as Error).message);
    } finally {
      const t1 = Date.now();
      setFinishedAt(new Date(t1).toISOString());
      setDurationMs(t1 - t0);
      setRunning(false);
    }
  };

  return (
    <AdminShell title="PRODUCTION SMOKE TEST">
      <p className="font-body text-muted-foreground max-w-3xl mb-6">
        Runs Stripe key + webhook checks, then a full booking → payment → confirmation email → webhook → refund cycle.
        Synthetic rows are cleaned up automatically. Safe to run against live mode (uses synthetic refund test, no real charge).
      </p>

      <div className="bg-panel border border-border p-6 mb-6 flex flex-wrap items-center gap-4">
        <Button onClick={run} disabled={running} size="lg" className="gap-2">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? "Running LIVE smoke test…" : "Run LIVE smoke test"}
        </Button>
        {startedAt && (
          <div className="text-xs font-mono text-muted-foreground">
            Started: {new Date(startedAt).toLocaleString()}
            {finishedAt && <> • Finished: {new Date(finishedAt).toLocaleString()}</>}
            {durationMs !== null && <> • {(durationMs / 1000).toFixed(2)}s</>}
          </div>
        )}
        {report && (
          <div className="ml-auto flex gap-2 items-center">
            <Badge variant={report.mode === "live" ? "default" : "secondary"}>{report.mode.toUpperCase()}</Badge>
            <Badge variant={report.ok ? "default" : "destructive"}>{report.passed}/{report.total} passed</Badge>
            {report.booking_id && <Badge variant="outline" className="font-mono text-xs">{report.booking_id.slice(0, 8)}</Badge>}
          </div>
        )}
      </div>

      {report && (
        <div className="bg-panel border border-border p-6 mb-6">
          <h3 className="font-display tracking-widest text-sm mb-4">CHECKS</h3>
          <ul className="space-y-3">
            {report.checks.map((c, i) => (
              <li key={i} className="flex gap-3 items-start text-sm font-body">
                {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> : <XCircle className="w-4 h-4 text-destructive mt-0.5" />}
                <div className="flex-1">
                  <p>
                    {c.name}
                    {c.critical && !c.ok && <Badge variant="destructive" className="ml-2 text-[10px]">CRITICAL</Badge>}
                  </p>
                  {c.detail && <p className="text-xs text-muted-foreground font-mono mt-0.5 break-all">{c.detail}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {log.length > 0 && (
        <div className="bg-panel border border-border p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display tracking-widest text-sm">RUN LOG</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const text = log.map(l => `[${l.ts}] ${l.msg}`).join("\n");
                navigator.clipboard.writeText(text);
                toast.success("Log copied");
              }}
            >
              Copy log
            </Button>
          </div>
          <pre className="text-xs font-mono bg-background border border-border p-3 max-h-96 overflow-auto whitespace-pre-wrap break-all">
{log.map(l => `[${new Date(l.ts).toLocaleTimeString()}] ${l.msg}`).join("\n")}
          </pre>
        </div>
      )}
    </AdminShell>
  );
}